import type { SpecType } from '@local/lib';

import { Combobox } from '@salesforce/design-system-react';
import { useState } from 'react';

import type { TemplateSpecConfig } from '../../quality-formulas/qualityFormulaTypes';

import { useTemplatesForSpec } from '../hooks/useTemplates';
import { fromTemplateConfig } from '../templateConfigConversion';

interface LoadTemplateControlProps {
  onApply: (config: TemplateSpecConfig) => void;
  specLabel: string;
  specType: SpecType;
}

/**
 * Per-spec "load a saved Template" control shown inside each Quality Spec card. Templates are
 * scoped to exactly one Quality Specification (see `templateTypes.ts`), so this only ever lists
 * templates that belong to *this* card's spec — a Moisture card never shows HGI templates, and
 * vice versa, with no per-spec code to maintain as new specs are added (see `useTemplatesForSpec`).
 *
 * This is the only template interaction available here — there's no Save/Delete; those live
 * exclusively on the Template Manager screen so a contract (or another template) can never
 * accidentally overwrite a shared template.
 *
 * Lives in the Templates feature (not `quality-formulas/components`, even though `QualitySpecCard`
 * is the only thing that renders it) since it's the one piece of UI that reaches into the
 * Templates feature's own data layer (`useTemplatesForSpec`/`fromTemplateConfig`) — `QualitySpecCard`
 * never imports it directly; its caller (e.g. `QualityFormulasPage`) supplies it via
 * `renderLoadTemplateControl` instead. Applying the loaded config is the caller's job too (via
 * `onApply`) — this component only ever reads from the Templates feature's own data layer, it
 * never reaches into the quality-formulas store, so the dependency between the two features stays
 * one-directional (Templates never needs to know quality-formulas' store exists).
 */
export const LoadTemplateControl: React.FC<LoadTemplateControlProps> = ({ onApply, specLabel, specType }) => {
  const { isLoading, templates } = useTemplatesForSpec(specType);
  const [selectedTemplateId, setSelectedTemplateId] = useState<null | string>(null);
  const options = templates.map(template => ({ id: template.id, label: template.name }));
  const selection = options.filter(option => option.id === selectedTemplateId);

  const handleSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templates.find(candidate => candidate.id === templateId);
    if (template) onApply(fromTemplateConfig(template.config));
  };

  return (
    <div className="qfw-load-template-control" style={{ alignItems: 'flex-end', display: 'flex', gap: 6 }}>
      <div className="qfw-load-template-control-field" style={{ width: 165 }}>
        <Combobox
          assistiveText={{ label: `Load ${specLabel} Template` }}
          disabled={isLoading}
          events={{
            onRequestRemoveSelectedOption: () => {
              setSelectedTemplateId(null);
            },
            onSelect: (_event, data) => {
              const selectedId = data.selection[0]?.id;
              if (selectedId) handleSelect(selectedId);
            },
          }}
          hasDeselect
          hasMenuSpinner={isLoading}
          id={`load-template-${specType}`}
          labels={{
            noOptionsFound: 'No templates found',
            placeholder: templates.length === 0 ? 'No templates yet' : 'Load Template',
          }}
          options={options}
          predefinedOptionsOnly
          selection={selection}
          variant="base"
        />
      </div>
    </div>
  );
};
